import React, { useContext } from "react";
import { observer } from "mobx-react-lite";
import { StoreContext } from "../../store";
import { Box, makeStyles } from "@material-ui/core";
import AccountExpiredAlert from "./alerts/AccountExpiredAlert";
import FeatureLockedAlert from "./alerts/FeatureLockedAlert";
import Page from "../Page";
import useTranslation from "next-translate/useTranslation";
import { toJS } from "mobx";

const useStyles = makeStyles((theme) => ({
  lockedContainer: {
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    width: "100%",
    backgroundColor: "#f8fafc",
    borderRadius: 16,
    border: "1px dashed #cbd5e0",
    marginTop: theme.spacing(2),
  },
  lockedSection: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    width: "100%",
    borderRadius: 16,
    marginTop: theme.spacing(2),
  },
}));

/**
 * FeatureGuard Component
 *
 * Handles conditional rendering based on subscription status and plan level.
 * Shows AccountExpiredAlert if the subscription is expired.
 * Shows FeatureLockedAlert if the current plan is insufficient.
 */
const FeatureGuard = observer(
  ({
    children,
    variant = "page", // page, section, component. default is page
    featureName = "This feature",
    minPlan = "standard", // "starter", "trial", "standard", or "premium"
    lockDescription,
    className = "",
  }) => {
    const { t } = useTranslation("common");
    const classes = useStyles();
    const store = useContext(StoreContext);

    // 1. Check for Account Expiry (Highest Priority)
    if (store.subscription.isAccessExpired) {
      if (variant === "section") {
        return (
          <Box className={`${classes.lockedSection} ${className}`}>
            <AccountExpiredAlert />
          </Box>
        );
      }

      return (
        <Page maxWidth="xl">
          <Box className={`${classes.lockedContainer} ${className}`}>
            <AccountExpiredAlert />
          </Box>
        </Page>
      );
    }

    // 2. Check for Plan Restriction
    const planLevels = { starter: 0, trial: 1, standard: 1, premium: 2 };
    const userLevel = planLevels[store.subscription.selected?.plan] ?? 0;
    const requiredLevel = planLevels[minPlan] ?? 1;

    if (userLevel < requiredLevel) {
      let title = `${featureName} ${lockDescription || "is Locked"}`;
      let description = `Your current ${store.subscription.selected?.plan} plan does not include ${featureName}. Please upgrade to a ${minPlan} plan to unlock this feature and start managing your property more efficiently.`;

      if (store.subscription.selected?.plan === "standard" && minPlan === "premium") {
        title = `${featureName} Requires Premium`;
        description = `${featureName} is an exclusive feature available in our Premium plan. Upgrade now to access advanced automation and tools.`;
      }

      if (variant === "section") {
        return (
          <Box className={`${classes.lockedSection} ${className}`}>
            <FeatureLockedAlert title={t(title)} description={t(description)} />
          </Box>
        );
      }

      return (
        <Page maxWidth="xl">
          <Box className={`${classes.lockedContainer} ${className}`}>
            <FeatureLockedAlert title={t(title)} description={t(description)} />
          </Box>
        </Page>
      );
    }

    // 3. Render children if all checks pass
    return <>{children}</>;
  },
);

export default FeatureGuard;
